import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaPhone, FaPassport, FaDollarSign, FaUserTie, FaSave, FaTimes } from 'react-icons/fa';
import DateInput from '../common/DateInput';

const TravelerForm = ({ traveler, campaigns, onSubmit, onCancel }) => {
  const { t } = useTranslation('travelers');
  const [formData, setFormData] = useState({
    fullName: '',
    primaryPhone: '',
    secondaryPhone: '',
    passportNumber: '',
    campaignId: '',
    totalAmount: '',
    paymentType: 'cash',
    agentName: '',
    agentCommission: '',
    paymentPlan: {
      downPayment: '',
      monthlyAmount: '',
      durationMonths: '',
      startDate: ''
    }
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (traveler) {
      setFormData({
        fullName: traveler.fullName || '',
        primaryPhone: traveler.primaryPhone || '',
        secondaryPhone: traveler.secondaryPhone || '',
        passportNumber: traveler.passportNumber || '',
        campaignId: traveler.campaignId || '',
        totalAmount: traveler.totalAmount?.toString() || '',
        paymentType: traveler.paymentType || 'cash',
        agentName: traveler.agentName || '',
        agentCommission: traveler.agentCommission?.toString() || '',
        paymentPlan: {
          downPayment: traveler.paymentPlan?.downPayment?.toString() || '',
          monthlyAmount: traveler.paymentPlan?.monthlyAmount?.toString() || '',
          durationMonths: traveler.paymentPlan?.durationMonths?.toString() || '',
          startDate: traveler.paymentPlan?.startDate ? traveler.paymentPlan.startDate.split('T')[0] : ''
        }
      });
    }
  }, [traveler]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('paymentPlan.')) {
      const planField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        paymentPlan: {
          ...prev.paymentPlan,
          [planField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.fullName.trim()) {
      newErrors.fullName = t('validation.fullNameRequired');
    }
    if (!formData.primaryPhone.trim()) {
      newErrors.primaryPhone = t('validation.primaryPhoneRequired');
    }
    if (!formData.passportNumber.trim()) {
      newErrors.passportNumber = t('validation.passportNumberRequired');
    }
    if (!formData.campaignId) {
      newErrors.campaignId = t('validation.campaignRequired');
    }
    if (!formData.totalAmount || parseFloat(formData.totalAmount) <= 0) {
      newErrors.totalAmount = t('validation.totalAmountRequired');
    }
    if (!formData.agentName.trim()) {
      newErrors.agentName = t('validation.agentNameRequired');
    }
    if (!formData.agentCommission || parseFloat(formData.agentCommission) < 0) {
      newErrors.agentCommission = t('validation.agentCommissionRequired');
    }

    // Phone number validation
    const phoneRegex = /^[+]?[0-9\s-()]+$/;
    if (formData.primaryPhone && !phoneRegex.test(formData.primaryPhone)) {
      newErrors.primaryPhone = t('validation.invalidPhoneFormat');
    }
    if (formData.secondaryPhone && !phoneRegex.test(formData.secondaryPhone)) {
      newErrors.secondaryPhone = t('validation.invalidPhoneFormat');
    }

    // Installment plan validation
    if (formData.paymentType === 'installments') {
      if (!formData.paymentPlan.downPayment || parseFloat(formData.paymentPlan.downPayment) < 0) {
        newErrors['paymentPlan.downPayment'] = t('validation.downPaymentRequired');
      }
      if (!formData.paymentPlan.monthlyAmount || parseFloat(formData.paymentPlan.monthlyAmount) <= 0) {
        newErrors['paymentPlan.monthlyAmount'] = t('validation.monthlyAmountRequired');
      }
      if (!formData.paymentPlan.durationMonths || parseInt(formData.paymentPlan.durationMonths) <= 0) {
        newErrors['paymentPlan.durationMonths'] = t('validation.durationRequired');
      }
      if (!formData.paymentPlan.startDate) {
        newErrors['paymentPlan.startDate'] = t('validation.startDateRequired');
      }

      // Validate payment plan totals
      const totalAmount = parseFloat(formData.totalAmount) || 0;
      const downPayment = parseFloat(formData.paymentPlan.downPayment) || 0;
      const monthlyAmount = parseFloat(formData.paymentPlan.monthlyAmount) || 0;
      const duration = parseInt(formData.paymentPlan.durationMonths) || 0;
      const totalInstallments = downPayment + (monthlyAmount * duration);

      if (totalInstallments !== totalAmount) {
        newErrors['paymentPlan.total'] = t('validation.paymentPlanMismatch', {
          expected: totalAmount,
          calculated: totalInstallments
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const submissionData = {
      fullName: formData.fullName.trim(),
      primaryPhone: formData.primaryPhone.trim(),
      secondaryPhone: formData.secondaryPhone.trim(),
      passportNumber: formData.passportNumber.trim().toUpperCase(),
      campaignId: formData.campaignId,
      totalAmount: parseFloat(formData.totalAmount),
      paymentType: formData.paymentType,
      agentName: formData.agentName.trim(),
      agentCommission: parseFloat(formData.agentCommission)
    };

    if (formData.paymentType === 'installments') {
      const downPayment = parseFloat(formData.paymentPlan.downPayment);
      const monthlyAmount = parseFloat(formData.paymentPlan.monthlyAmount);
      const durationMonths = parseInt(formData.paymentPlan.durationMonths);
      const startDate = new Date(formData.paymentPlan.startDate);
      
      // Calculate next payment date
      const nextPaymentDate = new Date(startDate);
      nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);

      submissionData.paymentPlan = {
        downPayment,
        monthlyAmount,
        durationMonths,
        startDate: startDate.toISOString(),
        nextPaymentDate: nextPaymentDate.toISOString(),
        paidInstallments: traveler?.paymentPlan?.paidInstallments || 0
      };
      
      // Calculate remaining balance (total - down payment for new travelers)
      submissionData.remainingBalance = traveler 
        ? traveler.remainingBalance 
        : submissionData.totalAmount - downPayment;
    } else {
      // For cash payments, remaining balance is 0 for new travelers
      submissionData.remainingBalance = traveler?.remainingBalance || 0;
    }

    onSubmit(submissionData);
  };

  const calculateInstallmentTotal = () => {
    const downPayment = parseFloat(formData.paymentPlan.downPayment) || 0;
    const monthlyAmount = parseFloat(formData.paymentPlan.monthlyAmount) || 0;
    const duration = parseInt(formData.paymentPlan.durationMonths) || 0;
    return downPayment + (monthlyAmount * duration);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <FaUser className="text-blue-600" />
            {traveler ? t('editTraveler') : t('addTraveler')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {traveler ? t('editTravelerSubtitle') : t('addTravelerSubtitle')}
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <FaTimes className="text-2xl" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('personalInformation')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('fullName')} *
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.fullName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('fullNamePlaceholder')}
              />
              {errors.fullName && (
                <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
              )}
            </div>

            {/* Campaign */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('campaign')} *
              </label>
              <select
                name="campaignId"
                value={formData.campaignId}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.campaignId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">{t('selectCampaign')}</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.campaignName}
                  </option>
                ))}
              </select>
              {errors.campaignId && (
                <p className="text-red-500 text-sm mt-1">{errors.campaignId}</p>
              )}
            </div>

            {/* Primary Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('primaryPhone')} *
              </label>
              <input
                type="tel"
                name="primaryPhone"
                value={formData.primaryPhone}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.primaryPhone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('primaryPhonePlaceholder')}
              />
              {errors.primaryPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.primaryPhone}</p>
              )}
            </div>

            {/* Secondary Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('secondaryPhone')}
              </label>
              <input
                type="tel"
                name="secondaryPhone"
                value={formData.secondaryPhone}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.secondaryPhone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('secondaryPhonePlaceholder')}
              />
              {errors.secondaryPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.secondaryPhone}</p>
              )}
            </div>

            {/* Passport Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('passportNumber')} *
              </label>
              <input
                type="text"
                name="passportNumber"
                value={formData.passportNumber}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.passportNumber ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('passportNumberPlaceholder')}
              />
              {errors.passportNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.passportNumber}</p>
              )}
            </div>

            {/* Total Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('totalAmount')} *
              </label>
              <input
                type="number"
                name="totalAmount"
                value={formData.totalAmount}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.totalAmount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('totalAmountPlaceholder')}
              />
              {errors.totalAmount && (
                <p className="text-red-500 text-sm mt-1">{errors.totalAmount}</p>
              )}
            </div>
          </div>
        </div>

        {/* Agent Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FaUserTie className="text-purple-600" />
            {t('agentInformation')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Agent Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('agentName')} *
              </label>
              <input
                type="text"
                name="agentName"
                value={formData.agentName}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.agentName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('agentNamePlaceholder')}
              />
              {errors.agentName && (
                <p className="text-red-500 text-sm mt-1">{errors.agentName}</p>
              )}
            </div>

            {/* Agent Commission */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('agentCommission')} *
              </label>
              <input
                type="number"
                name="agentCommission"
                value={formData.agentCommission}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.agentCommission ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('agentCommissionPlaceholder')}
              />
              {errors.agentCommission && (
                <p className="text-red-500 text-sm mt-1">{errors.agentCommission}</p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FaDollarSign className="text-green-600" />
            {t('paymentInformation')}
          </h3>
          
          {/* Payment Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('paymentType')} *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="paymentType"
                  value="cash"
                  checked={formData.paymentType === 'cash'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                {t('paymentTypes.cash')}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="paymentType"
                  value="installments"
                  checked={formData.paymentType === 'installments'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                {t('paymentTypes.installments')}
              </label>
            </div>
          </div>

          {/* Installment Plan Details */}
          {formData.paymentType === 'installments' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-blue-800 dark:text-blue-200">
                {t('installmentPlanDetails')}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Down Payment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('downPayment')} *
                  </label>
                  <input
                    type="number"
                    name="paymentPlan.downPayment"
                    value={formData.paymentPlan.downPayment}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors['paymentPlan.downPayment'] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder={t('downPaymentPlaceholder')}
                  />
                  {errors['paymentPlan.downPayment'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['paymentPlan.downPayment']}</p>
                  )}
                </div>

                {/* Monthly Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('monthlyAmount')} *
                  </label>
                  <input
                    type="number"
                    name="paymentPlan.monthlyAmount"
                    value={formData.paymentPlan.monthlyAmount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors['paymentPlan.monthlyAmount'] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder={t('monthlyAmountPlaceholder')}
                  />
                  {errors['paymentPlan.monthlyAmount'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['paymentPlan.monthlyAmount']}</p>
                  )}
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('durationMonths')} *
                  </label>
                  <input
                    type="number"
                    name="paymentPlan.durationMonths"
                    value={formData.paymentPlan.durationMonths}
                    onChange={handleInputChange}
                    min="1"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      errors['paymentPlan.durationMonths'] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder={t('durationPlaceholder')}
                  />
                  {errors['paymentPlan.durationMonths'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['paymentPlan.durationMonths']}</p>
                  )}
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('startDate')} *
                  </label>
                  <DateInput

 name="paymentPlan.startDate"
 value={formData.paymentPlan.startDate}
 onChange={handleInputChange}
 className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
 errors['paymentPlan.startDate'] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
 }`}
 />
                  {errors['paymentPlan.startDate'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['paymentPlan.startDate']}</p>
                  )}
                </div>
              </div>

              {/* Payment Plan Summary */}
              {formData.paymentPlan.downPayment && formData.paymentPlan.monthlyAmount && formData.paymentPlan.durationMonths && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">{t('paymentSummary')}</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('calculatedTotal')}:</span>
                      <span className="ml-2 font-medium">${calculateInstallmentTotal().toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('expectedTotal')}:</span>
                      <span className="ml-2 font-medium">${parseFloat(formData.totalAmount || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  {errors['paymentPlan.total'] && (
                    <p className="text-red-500 text-sm mt-2">{errors['paymentPlan.total']}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <FaTimes className="inline mr-2" />
            {t('cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <FaSave />
            {traveler ? t('updateTraveler') : t('createTraveler')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TravelerForm;